import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Loader2,
  LogOut,
  ShieldCheck,
  Power,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Phone,
  Lock,
  Plus,
  Trash2,
  Bike,
  Calendar as CalendarIcon,
  Clock,
} from "lucide-react";
import {
  adminGetBookingStatus,
  adminMe,
  adminSetBookingStatus,
  adminListModelBlocks,
  adminCreateModelBlock,
  adminDeleteModelBlock,
  clearAdminToken,
  getAdminToken,
} from "../lib/adminApi";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

const AdminDashboard = () => {
  const navigate = useNavigate();

  const [adminEmail, setAdminEmail] = useState("");
  const [online, setOnline] = useState(null); // null while loading
  const [initialising, setInitialising] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    document.title = "RR Bros · Admin";
    let meta = document.querySelector('meta[name="robots"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "robots");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", "noindex,nofollow");
    (async () => {
      if (!getAdminToken()) {
        navigate("/admin/login", { replace: true });
        return;
      }
      try {
        const me = await adminMe();
        setAdminEmail(me.email || "");
        const status = await adminGetBookingStatus();
        setOnline(status);
      } catch {
        clearAdminToken();
        navigate("/admin/login", { replace: true });
        return;
      } finally {
        setInitialising(false);
      }
    })();
  }, [navigate]);

  const logout = () => {
    clearAdminToken();
    navigate("/admin/login", { replace: true });
  };

  const applyChange = async (next) => {
    setError("");
    setNotice("");
    setSaving(true);
    try {
      const v = await adminSetBookingStatus(next);
      setOnline(v);
      setNotice(
        v
          ? "Online bookings are now ON. Customers can book normally."
          : "Online bookings are now OFF. Public Book Now is disabled site-wide."
      );
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Could not update booking status.");
    } finally {
      setSaving(false);
    }
  };

  const onToggleClick = () => {
    if (online) {
      // ON → OFF needs confirmation
      setConfirmOpen(true);
    } else {
      // OFF → ON is instant restore
      applyChange(true);
    }
  };

  if (initialising) {
    return (
      <div className="min-h-screen bg-neutral-950 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
      </div>
    );
  }

  const isOn = online === true;

  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100 antialiased">
      {/* Private admin top bar — not linked from public site */}
      <header className="bg-neutral-900/80 backdrop-blur border-b border-neutral-800 sticky top-0 z-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-orange-500/10 border border-orange-500/30 flex items-center justify-center">
              <ShieldCheck className="w-4 h-4 text-orange-400" />
            </div>
            <div>
              <p className="text-sm font-black tracking-tight">
                RR Bros · Admin
              </p>
              <p
                className="text-[10px] text-neutral-400 leading-none"
                data-testid="admin-email-display"
              >
                {adminEmail}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={logout}
            data-testid="admin-logout-btn"
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-neutral-800 hover:bg-neutral-700 text-xs font-bold uppercase tracking-wider text-neutral-200 transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" />
            Logout
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-10 space-y-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-black">Controls</h1>
          <p className="text-xs text-neutral-400 mt-1">
            Emergency switches that affect the public website in real time.
          </p>
        </div>

        {error && (
          <div
            role="alert"
            data-testid="admin-error"
            className="flex items-start gap-2 bg-red-500/10 border border-red-500/30 text-red-300 rounded-xl px-3 py-2 text-sm"
          >
            <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {notice && (
          <div
            role="status"
            data-testid="admin-notice"
            className="flex items-start gap-2 bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 rounded-xl px-3 py-2 text-sm"
          >
            <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>{notice}</span>
          </div>
        )}

        {/* ONLINE BOOKING card */}
        <section
          data-testid="online-booking-card"
          className="bg-neutral-900 border border-neutral-800 rounded-2xl p-5 sm:p-6"
        >
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-[10px] font-black uppercase tracking-[0.18em] text-orange-400">
                Online Booking
              </p>
              <h2 className="text-lg sm:text-xl font-black mt-1">
                Public booking flow
              </h2>
              <p className="text-xs text-neutral-400 mt-1 max-w-md">
                Flip this OFF to immediately stop every customer from reaching
                the Zoho booking form through the website. Scooter search still
                works, prices still show, Vehicle_Master and real fleet stay
                untouched.
              </p>
            </div>
            <div
              data-testid="status-pill"
              className={`inline-flex items-center gap-2 h-9 px-3 rounded-full text-xs font-black uppercase tracking-wider border ${
                isOn
                  ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300"
                  : "bg-red-500/15 border-red-500/40 text-red-300"
              }`}
            >
              <span
                className={`w-2 h-2 rounded-full ${
                  isOn ? "bg-emerald-400" : "bg-red-400"
                }`}
              />
              {isOn ? "ON" : "OFF"}
            </div>
          </div>

          <div className="mt-5 flex flex-col sm:flex-row sm:items-center gap-4 sm:justify-between bg-neutral-950 border border-neutral-800 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <Power
                className={`w-5 h-5 mt-0.5 ${
                  isOn ? "text-emerald-400" : "text-red-400"
                }`}
              />
              <div>
                <p className="text-sm font-bold">
                  {isOn ? "Accepting Bookings" : "Temporarily Closed"}
                </p>
                <p className="text-xs text-neutral-400 mt-0.5">
                  {isOn
                    ? "Customers can see live prices, open the booking review and continue to Zoho."
                    : "Customers see Currently Unavailable on every model and a call-us message."}
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={onToggleClick}
              disabled={saving}
              data-testid="toggle-booking-btn"
              className={`h-12 px-5 rounded-xl font-black text-sm uppercase tracking-wider inline-flex items-center justify-center gap-2 transition-colors ${
                isOn
                  ? "bg-red-500 hover:bg-red-600 text-white"
                  : "bg-emerald-500 hover:bg-emerald-600 text-white"
              } disabled:opacity-60`}
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Power className="w-4 h-4" />
              )}
              {isOn ? "Switch OFF" : "Switch ON"}
            </button>
          </div>
        </section>

        {/* When OFF — info card */}
        {!isOn && (
          <section
            data-testid="off-info-card"
            className="bg-red-500/5 border border-red-500/20 rounded-2xl p-5 sm:p-6"
          >
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <div className="space-y-2">
                <p className="text-sm font-bold text-red-200">
                  Public website is in Temporarily Closed mode
                </p>
                <ul className="text-xs text-red-200/80 space-y-1 list-disc pl-4">
                  <li>Search page still loads models and prices.</li>
                  <li>Every scooter card shows Currently Unavailable.</li>
                  <li>Book Now and the Review page are blocked.</li>
                  <li>
                    If a customer still tries, we re-check this flag just before
                    the Zoho redirect and block them.
                  </li>
                </ul>
                <div className="flex items-center gap-1.5 text-xs text-red-200 pt-1">
                  <Phone className="w-3.5 h-3.5" />
                  Customers are told to call{" "}
                  <span className="font-black">070677 06719</span>.
                </div>
              </div>
            </div>
          </section>
        )}

        <ModelBlocksCard />
      </main>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="confirm-off-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Stop online bookings?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to temporarily stop all online bookings?
              Customers can still search and see prices, but Book Now will be
              disabled across the entire website until you switch it back on.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-off-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-off-confirm"
              onClick={() => {
                setConfirmOpen(false);
                applyChange(false);
              }}
              className="bg-red-500 hover:bg-red-600"
            >
              Yes, switch OFF
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminDashboard;

// ====================================================================
// MODEL-WISE BOOKING BLOCK — second independent control.
// Admin can add any number of (model, start, end) blocks; customer-side
// enforcement happens in the backend `/search-availability` and
// `/booking/build-zoho-url` endpoints, so there is no way to bypass it
// through DevTools or by crafting a direct API call.
// ====================================================================
const BLOCKABLE_MODELS = [
  "Yamaha Ray ZR BS6",
  "Yamaha Fascino BS6",
  "Honda Activa 6G",
];

// Minimum datetime-local value for the pickers = right now (IST).
const istNowLocal = () => {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(new Date());
  const get = (t) => parts.find((p) => p.type === t)?.value;
  return `${get("year")}-${get("month")}-${get("day")}T${get("hour")}:${get("minute")}`;
};

const formatBlockLabel = (iso) => {
  if (!iso) return "";
  const [date, time] = iso.split("T");
  if (!date || !time) return iso;
  const [y, m, d] = date.split("-").map(Number);
  const [H, M] = time.split(":").map(Number);
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const period = H >= 12 ? "PM" : "AM";
  let h12 = H % 12;
  if (h12 === 0) h12 = 12;
  return `${String(d).padStart(2,"0")} ${months[m - 1]} ${y}, ${String(h12).padStart(2,"0")}:${String(M).padStart(2,"0")} ${period}`;
};

const ModelBlocksCard = () => {
  const [blocks, setBlocks] = useState(null); // null = loading
  const [loadError, setLoadError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null); // block being deleted
  const [deleting, setDeleting] = useState(false);
  const [notice, setNotice] = useState("");

  // Form state
  const defaultNow = istNowLocal();
  const [fModel, setFModel] = useState("");
  const [fStart, setFStart] = useState(defaultNow);
  const [fEnd, setFEnd] = useState(defaultNow);
  const [fReason, setFReason] = useState("");
  const [fSaving, setFSaving] = useState(false);
  const [fError, setFError] = useState("");

  const load = async () => {
    setLoadError("");
    try {
      const list = await adminListModelBlocks();
      setBlocks(list);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setLoadError(typeof d === "string" ? d : "Could not load blocks.");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const resetForm = () => {
    setFModel("");
    setFStart(istNowLocal());
    setFEnd(istNowLocal());
    setFReason("");
    setFError("");
  };

  const onSave = async (e) => {
    e.preventDefault();
    setFError("");
    if (!fModel) return setFError("Please select a vehicle model.");
    if (!fStart || !fEnd)
      return setFError("Please pick both start and end date/time.");
    if (fEnd <= fStart)
      return setFError("Block end must be after block start.");
    setFSaving(true);
    try {
      await adminCreateModelBlock(fModel, fStart, fEnd, fReason.trim() || null);
      setNotice("Block saved. The model is now unavailable for that period.");
      setShowForm(false);
      resetForm();
      await load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      setFError(typeof d === "string" ? d : "Could not save the block.");
    } finally {
      setFSaving(false);
    }
  };

  const onDelete = async (block) => {
    setDeleting(true);
    try {
      await adminDeleteModelBlock(block.id);
      setNotice("Block deleted. Normal availability resumed for that period.");
      setConfirmDel(null);
      await load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      setNotice("");
      setLoadError(typeof d === "string" ? d : "Could not delete the block.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <section
      data-testid="model-blocks-card"
      className="bg-neutral-900 border border-neutral-800 rounded-2xl p-5 sm:p-6"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-[10px] font-black uppercase tracking-[0.18em] text-orange-400">
            Model-Wise Booking Block
          </p>
          <h2 className="text-lg sm:text-xl font-black mt-1">
            Stop a specific scooter for a date range
          </h2>
          <p className="text-xs text-neutral-400 mt-1 max-w-md">
            Works alongside the master switch above. Blocks apply only to the
            selected model and date range. The master switch, when OFF, still
            trumps everything.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            resetForm();
            setShowForm((v) => !v);
          }}
          data-testid="add-block-btn"
          className="inline-flex items-center gap-1.5 h-10 px-4 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-black text-xs uppercase tracking-wider"
        >
          <Plus className="w-4 h-4" />
          {showForm ? "Close" : "Add New Block"}
        </button>
      </div>

      {notice && (
        <div
          role="status"
          data-testid="block-notice"
          className="mt-4 flex items-start gap-2 bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 rounded-xl px-3 py-2 text-sm"
        >
          <CheckCircle2 className="w-4 h-4 mt-0.5" />
          <span>{notice}</span>
        </div>
      )}
      {loadError && (
        <div
          role="alert"
          className="mt-4 flex items-start gap-2 bg-red-500/10 border border-red-500/30 text-red-300 rounded-xl px-3 py-2 text-sm"
        >
          <XCircle className="w-4 h-4 mt-0.5" />
          <span>{loadError}</span>
        </div>
      )}

      {showForm && (
        <form
          onSubmit={onSave}
          data-testid="add-block-form"
          className="mt-5 bg-neutral-950 border border-neutral-800 rounded-xl p-4 space-y-3"
        >
          {fError && (
            <div
              role="alert"
              data-testid="block-form-error"
              className="flex items-start gap-2 bg-red-500/10 border border-red-500/30 text-red-300 rounded-xl px-3 py-2 text-sm"
            >
              <XCircle className="w-4 h-4 mt-0.5" />
              <span>{fError}</span>
            </div>
          )}

          <div>
            <label className="text-[10px] font-black uppercase tracking-[0.14em] text-neutral-400 mb-1 block">
              Vehicle Model
            </label>
            <div className="relative">
              <Bike className="w-4 h-4 text-orange-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <select
                value={fModel}
                onChange={(e) => setFModel(e.target.value)}
                data-testid="block-model"
                className="w-full h-11 pl-10 pr-3 rounded-xl bg-neutral-900 border border-neutral-800 text-sm font-semibold text-neutral-100 outline-none focus:ring-2 focus:ring-orange-500/50"
              >
                <option value="">— Select a scooter —</option>
                {BLOCKABLE_MODELS.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-black uppercase tracking-[0.14em] text-neutral-400 mb-1 block">
                Block Start (IST)
              </label>
              <div className="relative">
                <CalendarIcon className="w-4 h-4 text-orange-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  type="datetime-local"
                  value={fStart}
                  onChange={(e) => setFStart(e.target.value)}
                  data-testid="block-start"
                  className="w-full h-11 pl-10 pr-3 rounded-xl bg-neutral-900 border border-neutral-800 text-sm font-semibold text-neutral-100 outline-none focus:ring-2 focus:ring-orange-500/50"
                />
              </div>
            </div>
            <div>
              <label className="text-[10px] font-black uppercase tracking-[0.14em] text-neutral-400 mb-1 block">
                Block End (IST)
              </label>
              <div className="relative">
                <Clock className="w-4 h-4 text-orange-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  type="datetime-local"
                  value={fEnd}
                  onChange={(e) => setFEnd(e.target.value)}
                  data-testid="block-end"
                  className="w-full h-11 pl-10 pr-3 rounded-xl bg-neutral-900 border border-neutral-800 text-sm font-semibold text-neutral-100 outline-none focus:ring-2 focus:ring-orange-500/50"
                />
              </div>
            </div>
          </div>

          <div>
            <label className="text-[10px] font-black uppercase tracking-[0.14em] text-neutral-400 mb-1 block">
              Reason (optional)
            </label>
            <input
              type="text"
              value={fReason}
              onChange={(e) => setFReason(e.target.value)}
              placeholder="e.g. maintenance, vehicle issue"
              data-testid="block-reason"
              className="w-full h-11 px-3 rounded-xl bg-neutral-900 border border-neutral-800 text-sm font-semibold text-neutral-100 outline-none focus:ring-2 focus:ring-orange-500/50"
            />
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="h-10 px-3 rounded-lg bg-neutral-800 hover:bg-neutral-700 text-xs font-bold uppercase tracking-wider text-neutral-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={fSaving}
              data-testid="save-block-btn"
              className="h-10 px-4 rounded-lg bg-orange-500 hover:bg-orange-600 disabled:bg-neutral-700 text-xs font-black uppercase tracking-wider text-white inline-flex items-center gap-1.5"
            >
              {fSaving ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Lock className="w-3.5 h-3.5" />
              )}
              Save Block
            </button>
          </div>
        </form>
      )}

      {/* Existing blocks list */}
      <div className="mt-5">
        <p className="text-[10px] font-black uppercase tracking-[0.14em] text-neutral-500 mb-2">
          Active &amp; Scheduled Blocks
        </p>
        {blocks === null ? (
          <div className="flex items-center gap-2 text-sm text-neutral-400 py-6">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : blocks.length === 0 ? (
          <div
            data-testid="blocks-empty"
            className="rounded-xl border border-dashed border-neutral-700 bg-neutral-950 p-5 text-center text-sm text-neutral-500"
          >
            No model blocks right now. Normal Zoho availability applies.
          </div>
        ) : (
          <ul className="space-y-2" data-testid="blocks-list">
            {blocks.map((b) => (
              <li
                key={b.id}
                data-testid={`block-row-${b.id}`}
                className="rounded-xl border border-neutral-800 bg-neutral-950 p-3 sm:p-4 flex items-start gap-3"
              >
                <div className="w-9 h-9 rounded-lg bg-orange-500/10 border border-orange-500/30 flex items-center justify-center flex-shrink-0">
                  <Bike className="w-4 h-4 text-orange-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-black text-neutral-100 truncate">
                    {b.model}
                  </p>
                  <p className="text-[11px] text-neutral-400 mt-0.5">
                    {formatBlockLabel(b.start_dt)}{" "}
                    <span className="text-neutral-600">→</span>{" "}
                    {formatBlockLabel(b.end_dt)}
                  </p>
                  {b.reason && (
                    <p className="text-[11px] text-amber-300 mt-1">
                      Reason: {b.reason}
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setConfirmDel(b)}
                  data-testid={`delete-block-${b.id}`}
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-red-500/10 border border-red-500/30 hover:bg-red-500/20 text-red-300 text-[11px] font-black uppercase tracking-wider"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Delete
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <AlertDialog open={!!confirmDel} onOpenChange={(open) => !open && setConfirmDel(null)}>
        <AlertDialogContent data-testid="confirm-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this block?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDel && (
                <>
                  <strong>{confirmDel.model}</strong> will become bookable again
                  for{" "}
                  <span className="whitespace-nowrap">
                    {formatBlockLabel(confirmDel.start_dt)} →{" "}
                    {formatBlockLabel(confirmDel.end_dt)}
                  </span>
                  , subject to normal Zoho availability and the master switch.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-delete-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmDel && onDelete(confirmDel)}
              disabled={deleting}
              data-testid="confirm-delete-confirm"
              className="bg-red-500 hover:bg-red-600"
            >
              {deleting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-1.5" />
              ) : (
                <Trash2 className="w-4 h-4 mr-1.5" />
              )}
              Delete block
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
};
